import React from 'react';
import { ArrowRight, Flame, FlaskConical, Lock as LockIcon } from 'lucide-react';
import { CRITICALITY, lockChip, VERDICTS } from './model';
import { useExploration } from './state';
import NexusBot from './nexus/NexusBot';
import { Chip, Code } from './ui';

// Partie légère du module Exploration, chargée avec la fiche projet : l'état partagé et la carte « Verrous » de
// l'Aperçu. Les onglets (React Flow, vues) se chargent à la demande (index.js).

export { ExplorationProvider, useExploration } from './state';

const VERDICT_DOT = { positif: 'bg-emerald-500', mitige: 'bg-amber-400', negatif: 'bg-rose-500', non_concluant: 'bg-slate-400' };

/** Carte de l'Aperçu : où en sont les verrous (levés, à lever, bloquants), ceux à regarder, les derniers résultats et
 * ce que Nexus propose (remplace la synthèse de l'exploration, retirée le 2026-10-08). */
export function LocksGlance({ openTab }) {
  const { status, bundle, ix, open, openArtefact, copilot } = useExploration();
  if (!status?.enabled || !bundle || !ix || !ix.byType.lock.length) return null;
  const locks = ix.byType.lock.filter((l) => !l.props.dismissed);
  const state = (l) => ix.lockState(l.id)?.state;
  const lifted = locks.filter((l) => state(l) === 'leve').length;
  const blocked = locks.filter((l) => state(l) === 'bloque').length;
  const rank = { critique: 0, elevee: 1, moyenne: 2, faible: 3, nulle: 4 };
  const watch = locks.filter((l) => state(l) !== 'leve')
    .sort((a, b) => (state(a) === 'bloque' ? -1 : 0) - (state(b) === 'bloque' ? -1 : 0)
      || (rank[ix.criticality(a.id)?.level || 'nulle'] - rank[ix.criticality(b.id)?.level || 'nulle']) || a.num - b.num)
    .slice(0, 4);
  const latest = [...(bundle.results || [])].filter((r) => r.verdict)
    .sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || ''))).slice(0, 3);
  const proposals = Object.values(copilot?.nodes || {}).reduce((n, s) => n + (s.items || []).length, 0);
  const goto = (id) => { openTab('arbre'); if (id != null) open(id); };
  return (
    <section className="mt-4 rounded-2xl border border-slate-200/80 bg-white px-4 py-4 shadow-sm sm:px-5">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800"><LockIcon size={16} className="text-amber-500" /> Verrous</h2>
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">{lifted} levé{lifted > 1 ? 's' : ''}</span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{locks.length - lifted} à lever</span>
        {blocked > 0 && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">{blocked} bloquant{blocked > 1 ? 's' : ''}</span>}
        {proposals > 0 && (
          <button type="button" onClick={() => goto()} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100">
            <NexusBot mood="suggesting" size={16} /> {proposals} proposition{proposals > 1 ? 's' : ''} de Nexus
          </button>
        )}
        <button type="button" onClick={() => goto()} className="ml-auto flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-indigo-700">Ouvrir <ArrowRight size={12} /></button>
      </div>
      <div className="grid gap-4 md:grid-cols-[3fr_2fr]">
        <div className="min-w-0">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">À regarder</p>
          {watch.length === 0 ? <p className="text-sm text-emerald-700">Tous les verrous sont levés.</p> : watch.map((lock) => {
            const crit = ix.criticality(lock.id)?.level;
            return (
              <button key={lock.id} type="button" onClick={() => goto(lock.id)} className="flex w-full items-center gap-1.5 rounded-lg px-1 py-1 text-left text-sm text-slate-700 hover:bg-slate-50 hover:text-indigo-700">
                {['critique', 'elevee'].includes(crit) ? <Flame size={13} className="shrink-0 text-rose-500" title={CRITICALITY[crit].label} /> : <LockIcon size={13} className="shrink-0 text-amber-400" />}
                <Code>{lock.code}</Code><span className="min-w-0 flex-1 truncate">{lock.title}</span>
                {ix.carrier(lock.id) != null && <span className="hidden max-w-[10rem] shrink-0 truncate text-[11px] text-slate-400 sm:inline">{ix.title(ix.carrier(lock.id))}</span>}
                <Chip {...lockChip(ix.lockState(lock.id))} />
              </button>
            );
          })}
        </div>
        <div className="min-w-0">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Derniers résultats</p>
          {latest.length === 0 ? <p className="text-sm text-slate-400">Aucun essai n’a encore tranché.</p> : latest.map((r) => (
            <button key={r.id} type="button" onClick={() => { openTab('arbre'); openArtefact(r.artefact_id); }}
              className="flex w-full items-center gap-1.5 rounded-lg px-1 py-1 text-left text-sm text-slate-700 hover:bg-slate-50 hover:text-indigo-700">
              <FlaskConical size={13} className="shrink-0 text-indigo-400" />
              <Code>{ix.code(r.artefact_id)}</Code>
              <span className="min-w-0 flex-1 truncate">{ix.title(r.artefact_id)}</span>
              <span className="inline-flex shrink-0 items-center gap-1 text-xs text-slate-500"><span className={`h-2 w-2 rounded-full ${VERDICT_DOT[r.verdict]}`} />{ix.code(r.lock_id)} · {VERDICTS[r.verdict]?.label}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
